import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CaretDown, CaretUp, FileArrowUp, CalendarBlank, Trash } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${MONTHS[parseInt(m) - 1]} ${y}`;
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const ImportHistory = ({ type, onImportDeleted }) => {
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = type ? { type } : {};
      const { data } = await axios.get(`${BACKEND_URL}/api/sales/import-history`, { params, withCredentials: true });
      setHistory(data.history || []);
    } catch (err) { console.error('Failed to load import history:', err.message); }
    finally { setLoading(false); }
  }, [type]);

  useEffect(() => {
    if (expanded && history.length === 0) fetchHistory();
  }, [expanded, history.length, fetchHistory]);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/sales/import-history/${deleteItem.id}`, { withCredentials: true });
      toast.success(data.message);
      setHistory(prev => prev.filter(h => h.id !== deleteItem.id));
      setDeleteItem(null);
      if (onImportDeleted) onImportDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete import');
    } finally { setDeleting(false); }
  };

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="import-history-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors text-left"
        data-testid="import-history-toggle"
      >
        <div className="flex items-center gap-2.5">
          <FileArrowUp size={18} weight="regular" className="text-muted-foreground" />
          <span className="text-sm font-semibold tracking-tight">Import History</span>
          {history.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground font-bold">{history.length}</span>
          )}
        </div>
        {expanded ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-5 pb-4 border-t border-border">
          {loading ? (
            <div className="text-center py-6 text-sm text-muted-foreground">Loading...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">No imports recorded yet</div>
          ) : (
            <div className="space-y-2 mt-3 max-h-80 overflow-y-auto">
              {history.map((item) => (
                <div key={item.id} className="border border-border rounded-sm p-3 text-sm" data-testid={`import-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.filename}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{formatTimestamp(item.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm font-bold uppercase ${
                        item.type === 'sales' ? 'bg-primary/10 text-primary' : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                      }`}>{item.type}</span>
                      <button
                        onClick={() => setDeleteItem(item)}
                        className="p-1 hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-sm transition-colors"
                        title="Delete import and all its records"
                        data-testid={`delete-import-${item.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    {(item.date_range_start || item.date_range_end) && (
                      <div className="flex items-center gap-1.5 text-xs" data-testid={`import-date-range-${item.id}`}>
                        <CalendarBlank size={13} className="text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {item.date_range_start === item.date_range_end
                            ? formatDate(item.date_range_start)
                            : `${formatDate(item.date_range_start)} — ${formatDate(item.date_range_end)}`
                          }
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span><strong className="text-foreground">{item.imported}</strong> imported</span>
                      {item.duplicates > 0 && <span>{item.duplicates} duplicates</span>}
                      {item.skipped > 0 && <span>{item.skipped} skipped</span>}
                      {item.created_clients > 0 && <span>{item.created_clients} new clients</span>}
                    </div>
                  </div>

                  {item.column_mapping && (
                    <div className="mt-2 text-[10px] text-muted-foreground">
                      Columns: {Object.entries(item.column_mapping).map(([k, v]) => `"${v}" → ${k}`).join(' | ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => { if (!open) setDeleteItem(null); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="text-2xl tracking-tight">Delete Import</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              This will permanently delete <strong>{deleteItem?.imported}</strong> {deleteItem?.type} records imported from <strong>{deleteItem?.filename}</strong>.
            </p>
            {deleteItem?.date_range_start && (
              <p className="text-sm text-muted-foreground">
                Date range: {formatDate(deleteItem.date_range_start)} — {formatDate(deleteItem.date_range_end)}
              </p>
            )}
            <p className="text-sm text-destructive font-medium">This action cannot be undone. You can re-upload the file afterwards.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteItem(null)} className="flex-1 rounded-sm">Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="flex-1 rounded-sm" data-testid="confirm-delete-import">
                {deleting ? 'Deleting...' : 'Delete Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportHistory;
